import { useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';

import BpkInput from '@skyscanner/backpack-web/bpk-component-input';
import BpkModal from '@skyscanner/backpack-web/bpk-component-modal';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import type { OnUpdateDetailPageUrl } from '../HotelOffers/types';
import type { OnStayChange } from '../types';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';
import type { EntityId, Stay } from '@shared/types/utils';
import type { DomEvent } from '@shared/ui/common/RouteLink/RouteLink';

import STYLES from './HotelStayMobile.module.scss';

import { useHotelOffersActions } from '@hotels-hotel-detail/data-access/hooks/useHotelOffersContext';
import CTAButton from '@hotels-hotel-detail/ui/CTAButton';
import {
  HASH_HOTEL_STAYDATE,
  HASH_HOTEL_STAYINFO,
} from '@shared/constants/hash-routes';
import GuestsRoomsChildren from '@shared/hotels-search-controls/SearchControls/GuestsRoomsChildrenSelect/GuestsRoomsChildren';
import ScrollableDateRangeSelector from '@shared/hotels-search-controls/SearchControls/ScrollableDateRangeSelector';
import HashRoute, { withHashRoutePortal } from '@shared/ui/common/HashRoute';
import RouteLink from '@shared/ui/common/RouteLink';
import FullScreenWrapper from '@shared/ui/components/FullScreenWrapper';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import { getApplicationElement } from '@shared/utils-frontend/ApplicationElements';
import getGuestsRoomsChildrenValue from '@shared/utils-frontend/getGuestsRoomsChildrenValue';
import {
  ACTION_TYPE,
  CHANGED_CONDITION,
} from '@shared/utils-frontend/mini-events/constants';
import { buildAdditionalInfoSearchConditionChanged } from '@shared/utils-frontend/mini-events/hotels-action';
import { DATE_FORMAT } from '@shared/utils-universal/i18n/date-formats';

const cls = cssModules(STYLES);
const FullScreenRoute = withHashRoutePortal(FullScreenWrapper);

type Props = {
  miniEventTracker: MiniEventTracker;
  i18n: I18nService;
  stay: Stay;
  onStayChange: OnStayChange;
  hotelId: EntityId;
  ubffEnabled?: boolean;
  updateDetailPageUrl?: OnUpdateDetailPageUrl;
};

const HotelStayMobile = ({
  hotelId,
  i18n,
  miniEventTracker,
  onStayChange,
  stay: { checkIn, checkOut, childrenAges, numberOfAdults, numberOfRooms },
  ubffEnabled,
  updateDetailPageUrl,
}: Props) => {
  const hotelStayMobileRef = useRef<HTMLDivElement>(null);
  const { applyStay } = useHotelOffersActions();

  const initialDate = {
    selectedCheckIn: checkIn,
    selectedCheckOut: checkOut,
  };
  const initialGuestRoom = {
    selectedGuests: numberOfAdults,
    selectedRooms: numberOfRooms,
    selectedChildrenAges: childrenAges,
    roomNumChanged: false,
    adultChanged: false,
    childrenAgesChanged: false,
  };
  const dateRef = useRef(initialDate);
  const guestRoomRef = useRef(initialGuestRoom);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedGuestRoom, setSelectedGuestRoom] = useState(initialGuestRoom);

  const traceConditionChangedEvent = (changedCondition: string) => {
    const {
      adultChanged,
      childrenAgesChanged,
      roomNumChanged,
      selectedChildrenAges,
      selectedRooms,
    } = guestRoomRef.current;

    miniEventTracker.trackHotelsAction(
      ACTION_TYPE.SEARCH_CONDITION_CHANGED,
      buildAdditionalInfoSearchConditionChanged({
        hotelId,
        changedCondition,
        adultChanged,
        childrenAgesChanged,
        roomNumChanged,
        rooms: selectedRooms,
        isSearchWithChildren:
          selectedChildrenAges && selectedChildrenAges.length > 0,
      }),
    );
  };

  const onApplyChanged = () => {
    const { selectedChildrenAges, selectedGuests, selectedRooms } =
      guestRoomRef.current;
    const { selectedCheckIn, selectedCheckOut } = dateRef.current;

    const newStay = {
      checkIn: selectedCheckIn,
      checkOut: selectedCheckOut,
      numberOfAdults: parseInt(`${selectedGuests}`, 10),
      numberOfRooms: parseInt(`${selectedRooms}`, 10),
      childrenAges: selectedChildrenAges,
    };

    onStayChange(newStay);

    if (ubffEnabled && updateDetailPageUrl) {
      applyStay(newStay);

      updateDetailPageUrl({ updatedStay: newStay });
    }
  };

  const handleChangedDate = (startDate: Date, endDate: Date) => {
    dateRef.current = {
      selectedCheckIn: startDate,
      selectedCheckOut: endDate,
    };
    setSelectedDate((prevState) => ({
      ...prevState,
      ...dateRef.current,
    }));

    onApplyChanged();
    traceConditionChangedEvent(CHANGED_CONDITION.DATE_RANGE);
  };

  const handleGuestsRoomsChildrenChange = (
    rooms: number,
    adults: number,
    changedChildrenAges: number[],
  ) => {
    const { selectedChildrenAges, selectedGuests, selectedRooms } =
      guestRoomRef.current;

    if (adults !== selectedGuests) {
      const adultInfo = { selectedGuests: adults, adultChanged: true };
      setSelectedGuestRoom((prevState) => ({
        ...prevState,
        ...adultInfo,
      }));
      guestRoomRef.current = { ...guestRoomRef.current, ...adultInfo };
    }

    if (rooms !== selectedRooms) {
      const roomInfo = { selectedRooms: rooms, roomNumChanged: true };
      setSelectedGuestRoom((prevState) => ({
        ...prevState,
        ...roomInfo,
      }));
      guestRoomRef.current = { ...guestRoomRef.current, ...roomInfo };
    }

    if (!isEqual(changedChildrenAges, selectedChildrenAges)) {
      const childrenInfo = {
        selectedChildrenAges: changedChildrenAges,
        childrenAgesChanged: true,
      };
      setSelectedGuestRoom((prevState) => ({
        ...prevState,
        ...childrenInfo,
      }));
      guestRoomRef.current = { ...guestRoomRef.current, ...childrenInfo };
    }
  };

  const { selectedChildrenAges, selectedGuests, selectedRooms } =
    selectedGuestRoom;
  const isDisabled =
    numberOfAdults === selectedGuests &&
    numberOfRooms === selectedRooms &&
    isEqual(childrenAges, selectedChildrenAges);

  const { selectedCheckIn, selectedCheckOut } = selectedDate;

  const stayValue = i18n.translate('HotelStay_label_stayDates', {
    start_date: i18n.formatDate(
      selectedCheckIn,
      DATE_FORMAT.MONTH_DAY_NO_WEEK_ABBR,
    ),
    end_date: i18n.formatDate(
      selectedCheckOut,
      DATE_FORMAT.MONTH_DAY_NO_WEEK_ABBR,
    ),
  });

  return (
    <div className={cls('HotelStayMobile')} ref={hotelStayMobileRef}>
      <RouteLink
        href={HASH_HOTEL_STAYDATE}
        render={(onClickLink) => (
          <BpkInput
            id="hotel-stay-input"
            name="hotel-stay-input"
            className={cls(
              'HotelStayMobile__clickableArea',
              'HotelStayMobile__clickableArea--left',
            )}
            data-testid="edit-stay-date-button"
            value={stayValue}
            onClick={onClickLink as DomEvent}
            aria-label={stayValue}
            readOnly
          />
        )}
      />
      <RouteLink
        href={HASH_HOTEL_STAYINFO}
        render={(onClickLink) => (
          <BpkInput
            id="edit-stay-info"
            name="edit-stay-info-button"
            className={cls(
              'HotelStayMobile__clickableArea',
              'HotelStayMobile__clickableArea--right',
            )}
            data-testid="edit-stay-info-button"
            value={getGuestsRoomsChildrenValue({
              adults: numberOfAdults,
              rooms: numberOfRooms,
              childrenAges,
              translatePlural: i18n.translatePlural,
            })}
            aria-label={i18n.translate('HotelStay_label_guests_rooms')}
            onClick={onClickLink}
            readOnly
          />
        )}
      />
      <HashRoute
        hash={HASH_HOTEL_STAYDATE}
        render={({ onClose }) => (
          <BpkModal
            className={cls('HotelStayMobile__dateRangeSelectorModal')}
            onClose={onClose}
            title={i18n.translate('Explore_label_Dates')}
            closeLabel={i18n.translate('Modal_close')}
            isOpen
            getApplicationElement={getApplicationElement}
            id="hotel-stay-date-range-selector"
          >
            <ScrollableDateRangeSelector
              startDate={selectedCheckIn}
              endDate={selectedCheckOut}
              onClose={onClose}
              onApply={handleChangedDate}
            />
          </BpkModal>
        )}
      />
      <FullScreenRoute
        hash={HASH_HOTEL_STAYINFO}
        renderFooter={(onClose: Function) => (
          <CTAButton
            text={i18n.translate('hdb_save')}
            onClick={() => {
              onApplyChanged();
              traceConditionChangedEvent(CHANGED_CONDITION.GUEST_OR_ROOMS);
              onClose();
            }}
            disabled={isDisabled}
            textStyle={TEXT_STYLES.heading4}
            buttonClassName={cls('HotelStayMobile__confirmButton')}
          />
        )}
        footerClassName={cls('HotelStayMobile__modalFooter')}
        showCloseHeader
      >
        <>
          <BpkText
            textStyle={TEXT_STYLES.heading4}
            tagName="p"
            className={cls('HotelStayMobile__modalTitle')}
          >
            {i18n.translate('label_editDetails')}
          </BpkText>
          <div className={cls('HotelStayMobile__modelList')}>
            <GuestsRoomsChildren
              adults={numberOfAdults}
              rooms={numberOfRooms}
              childrenAges={childrenAges}
              onGuestsRoomsChildrenChanged={handleGuestsRoomsChildrenChange}
            />
          </div>
        </>
      </FullScreenRoute>
    </div>
  );
};

export default withMiniEventTracker(withI18n(HotelStayMobile));
